import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Skeleton } from '@/components/ui/skeleton';
import { getChangelog, type Release } from '@/lib/changelog';
import { formatDateTime } from '@/lib/date';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import { ArrowLeft, ChevronDown, ExternalLink, Gift, Megaphone, Tag } from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/settings/changelog')({
  beforeLoad: () => {
    throw redirect({ to: '/settings', search: { tab: 'changelog' } });
  },
});

function relativeTime(date: string): string {
  const now = Date.now();
  const then = new Date(date).getTime();
  const diffDays = Math.floor((now - then) / 86_400_000);

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
}

function ReleaseBody({ body }: { body: string }) {
  if (!body.trim()) return null;

  const lines = body.split('\n');
  const elements: React.ReactNode[] = [];
  let i = 0;

  for (const line of lines) {
    const key = i++;

    if (line.startsWith('### ')) {
      elements.push(
        <h4
          key={key}
          className="mt-4 mb-1.5 text-xs font-semibold uppercase tracking-wide text-foreground"
        >
          {line.slice(4)}
        </h4>
      );
    } else if (line.startsWith('## ')) {
      elements.push(
        <h3 key={key} className="mt-4 mb-1.5 text-sm font-semibold text-foreground">
          {line.slice(3)}
        </h3>
      );
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      const text = line.slice(2);
      const indent = line.startsWith('  ') ? 'ml-8' : 'ml-4';
      const boldMatch = text.match(/^\*\*(.+?)\*\*\s*[—–-]\s*(.*)/);
      elements.push(
        <li
          key={key}
          className={cn(indent, 'text-[13px] leading-relaxed text-muted-foreground list-disc')}
        >
          {boldMatch ? (
            <>
              <span className="font-medium text-foreground">{boldMatch[1]}</span>
              {' — '}
              {boldMatch[2]}
            </>
          ) : (
            formatInlineMarkdown(text)
          )}
        </li>
      );
    } else if (line.startsWith('  - ') || line.startsWith('  * ')) {
      const text = line.slice(4);
      elements.push(
        <li
          key={key}
          className="ml-8 text-[13px] leading-relaxed text-muted-foreground list-[circle]"
        >
          {formatInlineMarkdown(text)}
        </li>
      );
    } else if (line.trim() === '') {
      elements.push(<div key={key} className="h-1" />);
    } else {
      elements.push(
        <p key={key} className="text-[13px] leading-relaxed text-muted-foreground">
          {formatInlineMarkdown(line)}
        </p>
      );
    }
  }

  return <div className="space-y-0.5">{elements}</div>;
}

function formatInlineMarkdown(text: string): React.ReactNode {
  const parts = text.split(/(`[^`]+`)/g);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    part.startsWith('`') && part.endsWith('`') ? (
      <code key={i} className="rounded bg-muted px-1 py-0.5 text-[12px] font-mono text-foreground">
        {part.slice(1, -1)}
      </code>
    ) : (
      part
    )
  );
}

function ReleaseCard({
  release,
  isLatest,
  defaultOpen,
}: {
  release: Release;
  isLatest: boolean;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="relative flex gap-4">
      {/* Timeline */}
      <div className="flex flex-col items-center pt-1">
        <div
          className={cn(
            'h-3 w-3 shrink-0 rounded-full border-2',
            isLatest ? 'border-hygge bg-hygge/20' : 'border-border bg-background'
          )}
        />
        <div className="w-px flex-1 bg-border/50" />
      </div>

      {/* Content */}
      <Collapsible open={open} onOpenChange={setOpen} className="mb-4 flex-1">
        <Card className={cn('overflow-hidden transition-colors', isLatest && 'border-hygge/30')}>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer select-none hover:bg-muted/30 transition-colors pb-3">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1.5 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge
                      variant="outline"
                      className={cn(
                        'gap-1 text-xs font-mono',
                        isLatest && 'border-hygge/40 text-hygge'
                      )}
                    >
                      <Tag className="h-3 w-3" />
                      {release.tag}
                    </Badge>
                    {isLatest && (
                      <Badge className="gap-1 bg-hygge/10 text-hygge hover:bg-hygge/20 border-0 text-[10px]">
                        <Gift className="h-3 w-3" />
                        Latest
                      </Badge>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {relativeTime(release.publishedAt)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-semibold tracking-tight text-foreground">
                      {release.name}
                    </h3>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {formatDateTime(release.publishedAt, {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0 pt-0.5">
                  <a
                    href={release.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                  <div className="inline-flex h-7 w-7 items-center justify-center">
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 text-muted-foreground transition-transform duration-200',
                        open && 'rotate-180'
                      )}
                    />
                  </div>
                </div>
              </div>
            </CardHeader>
          </CollapsibleTrigger>

          <CollapsibleContent>
            {release.body && (
              <CardContent className="pt-0 pb-4">
                <div className="rounded-lg bg-muted/30 p-4">
                  <ReleaseBody body={release.body} />
                </div>
              </CardContent>
            )}
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </div>
  );
}

function ChangelogSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex gap-4">
          <div className="flex flex-col items-center pt-1">
            <Skeleton className="h-3 w-3 rounded-full" />
            <Skeleton className="w-px flex-1" />
          </div>
          <Card className="mb-4 flex-1">
            <CardHeader className="space-y-3 pb-3">
              <div className="flex items-center gap-2">
                <Skeleton className="h-5 w-20" />
                <Skeleton className="h-4 w-24" />
              </div>
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-3 w-32" />
            </CardHeader>
          </Card>
        </div>
      ))}
    </div>
  );
}

export function ChangelogPage() {
  const {
    data: releases = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['changelog'],
    queryFn: getChangelog,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Link
          to="/settings"
          className="mt-1 inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div>
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-hygge/10 p-2">
              <Megaphone className="h-5 w-5 text-hygge" />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">What's New</h1>
          </div>
          <p className="mt-1 ml-11 text-sm text-muted-foreground">
            Release notes and recent updates to HyggeOS
          </p>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <ChangelogSkeleton />
      ) : error ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-sm text-muted-foreground">
              Failed to load changelog. Please try again later.
            </p>
          </CardContent>
        </Card>
      ) : releases.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-muted">
              <Megaphone className="h-6 w-6 text-muted-foreground/60" />
            </div>
            <h4 className="mt-4 text-sm font-medium text-foreground">No Releases Yet</h4>
            <p className="mt-1 text-xs text-muted-foreground">
              Release notes will appear here when new versions are published.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div>
          {releases.map((release, index) => (
            <ReleaseCard
              key={release.tag}
              release={release}
              isLatest={index === 0}
              defaultOpen={index === 0}
            />
          ))}
        </div>
      )}
    </div>
  );
}
